import { LinkButton, Text } from "@ttc3k/trekker";
import { Product, EnumPriceTierPriceType } from "gql/generated";
import { PropsWithChildren, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getLowestNightlyPrice, formatPrice } from "shared/utils";
import { Flex, Grid } from "styled-system/jsx";
import { OverlayPage } from "../OverlayPages";
import React from "react";

type PriceDetailsProps = {
  pricing: Product["pricing"];
};

export const PriceDetails = ({ pricing }: PriceDetailsProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation("booker", {
    keyPrefix: "PRODUCT_DETAILS",
  });
  const isPerGuestPricing = pricing?.tiers.some(
    tier => tier?.priceType === EnumPriceTierPriceType.PerGuest,
  );

  const lowestNightlyPrice = getLowestNightlyPrice(pricing);

  const priceText = isPerGuestPricing
    ? `${t("STARTING_AT")} $${formatPrice(lowestNightlyPrice ?? 0)}/${t("NIGHT")}`
    : `$${formatPrice(pricing?.tiers[0]?.price ?? 0)}/${t("NIGHT")}`;

  const perGuestPricingDetails = useMemo(() => {
    return (pricing?.tiers ?? [])
      .filter(tier => !!tier)
      .reduce(
        (acc, tier, _, array) => {
          // There might be multiple guest types with the same per guest
          // pricing ranges, so we will just take the tiers from the first
          // guest type and ignore the rest since they will all be the same.
          const defaultGuestType = array[0].type;
          if (tier.type === defaultGuestType) {
            acc.push({
              _id: tier._id,
              nightlyTotal: tier.price * (tier.guestCountForPrice ?? 1),
              guestCount: tier.guestCountForPrice ?? 1,
            });
          }
          return acc;
        },
        [] as { _id: string; nightlyTotal: number; guestCount: number }[],
      );
  }, [pricing]);

  return (
    <Flex flexDir={"column"} alignItems={"flex-end"} gap={"50"}>
      <Text color={"text.mid"}>{priceText}</Text>
      {isPerGuestPricing && (
        <LinkButton onClick={() => setIsModalOpen(true)} size={"sm"}>
          {t("SEE_PRICE_DETAILS")}
        </LinkButton>
      )}

      <OverlayPage
        title={t("PRICING_STRUCTURE.TITLE")}
        subtitle={t("PRICING_STRUCTURE.SUBTITLE")}
        onButtonClick={() => setIsModalOpen(false)}
        onClose={() => setIsModalOpen(false)}
        open={isModalOpen}
      >
        <Grid gridTemplateColumns={"1fr 1fr"} gap={"50"} w={"full"} mt={"250"}>
          <GridItem isHeader>{t("PRICING_STRUCTURE.GUESTS")}</GridItem>
          <GridItem isHeader>{t("PRICING_STRUCTURE.NIGHTLY_TOTAL")}</GridItem>
          {perGuestPricingDetails.map((detail, index) => (
            <React.Fragment key={detail._id + index.toString()}>
              <GridItem isLeftColumn isOddRow={index % 2 === 0}>
                {detail.guestCount}
              </GridItem>
              <GridItem isOddRow={index % 2 === 0}>
                ${formatPrice(detail.nightlyTotal)}
              </GridItem>
            </React.Fragment>
          ))}
        </Grid>
      </OverlayPage>
    </Flex>
  );
};

type GridItemProps = PropsWithChildren & {
  isHeader?: boolean;
  isOddRow?: boolean;
  isLeftColumn?: boolean;
};
const GridItem = ({
  children,
  isHeader = false,
  isOddRow = false,
  isLeftColumn = false,
}: GridItemProps) => {
  return (
    <Flex
      width={"full"}
      px={"200"}
      py={"150"}
      bg={isOddRow ? "bg.lightest" : "bg.subtlest"}
      alignItems={"center"}
      justifyContent={"space-between"}
      h={"40px"}
    >
      <Text
        visual={isHeader ? "bodySemiBold" : "bodyRegular"}
        color={isLeftColumn || isHeader ? "text.light" : "text.dark"}
        lineClamp={1}
      >
        {children}
      </Text>
    </Flex>
  );
};
